import "devextreme/data/odata/store";
import {
    Column,
    DataGrid,
    FilterRow,
    HeaderFilter,
    Scrolling,
    Toolbar,
    Item,
    SearchPanel,
    Paging,
    Selection,
} from "devextreme-react/data-grid";
import SearchIcon from "@mui/icons-material/Search";
import { SelectionChangedEvent } from "devextreme/ui/data_grid";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Pagination, Button, IconButton } from "@mui/material";
import { defaultPageSize } from "../../constants";
import { SnackbarMessage, useSnackbar } from "../../hooks";
import { calculateGridHeight, convertUTCTimeToLocal } from "../../util/Utils";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { BulkSiteGridProps } from "./BulkSiteCreation.types";
import { ModalBulkSiteCreationDetail } from "./ModalBulkSiteCreationDetail";
import { hubConnection } from "../../signalr/signalr-connection";
import { signalRMethods } from "../../constants";
import { createSitesClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { envProperties } from "../../util/EnvironmentProperties";

const initialStateData = { pending: true, sites: [], totalCount: 0 };
const appName = "TP";
const yesNoLookup = {
    dataSource: [
        { value: true, text: "Yes" },
        { value: false, text: "No" },
    ],
    displayExpr: "text",
    valueExpr: "value",
};

interface SiteNameCellProps {
    hqSiteId: number;
    hqSiteName: string;
    getSiteUrl: (siteId: number) => string;
    handleCopySiteLinkClick: (event: React.MouseEvent<SVGSVGElement>, siteId: number) => void;
}

const SiteNameCell: React.FC<SiteNameCellProps> = ({ hqSiteId, hqSiteName, getSiteUrl, handleCopySiteLinkClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    if (hqSiteId === 0) {
        return <div>{hqSiteName}</div>;
    }

    return (
        <span
            style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                width: "100%",
                overflow: "hidden",
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <a
                href={getSiteUrl(hqSiteId)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    color: "#009cde",
                    textDecoration: "underline",
                    cursor: "pointer",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: "1 1 auto",
                    minWidth: 0,
                }}
            >
                {hqSiteName}
            </a>
            <ContentCopyIcon
                onClick={(event) => handleCopySiteLinkClick(event, hqSiteId)}
                titleAccess="Copy link"
                style={{
                    fontSize: "16px",
                    flexShrink: 0,
                    opacity: isHovered ? 1 : 0,
                    transition: "opacity 0.15s ease",
                    cursor: "pointer",
                    pointerEvents: isHovered ? "auto" : "none",
                }}
            />
        </span>
    );
};


export const BulkSitesGrid = ({
    userEmail,
    setBulkSiteMainPage,
}: BulkSiteGridProps) => {
    const [pageNumber, setPageNumber] = useState(1);
    const { showSnackbar } = useSnackbar();
    const [searchTerm, setSearchTerm] = useState<string | null | undefined>(null);
    const [siteId, setSiteId] = useState<string>();
    const [isCancelRequestEnabled, setIsCancelRequestEnabled] = useState(false);
    const [gridData, setSitesData] = useState<any>(initialStateData);
    const [isDisplayModal, setIsDisplayModal] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<any>([]);
    const [selectedRowData, setSelectedRowData] = useState<any>([]);
    const prevKeysRef = useRef<any>([]);
    const prevDataRef = useRef<any>([]);
    const [isExportEnabled, setIsExportEnabled] = useState(false);

    const getSiteUrl = useCallback((siteId: number) => `${envProperties.reactAppHighQSiteUrl}=${siteId}`, []);

    const handleCopySiteLinkClick = useCallback((event: React.MouseEvent<SVGSVGElement>, siteId: number) => {
        event.preventDefault();
        event.stopPropagation();
        const url = getSiteUrl(siteId);

        navigator.clipboard
            .writeText(url)
            .catch(() => {
                showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
            });
    }, [getSiteUrl, showSnackbar]);

    const cellRenderSiteName = useCallback((e: { data: any }) => {
        const { hqSiteId, hqSiteName } = e.data;
        return <SiteNameCell hqSiteId={hqSiteId} hqSiteName={hqSiteName} getSiteUrl={getSiteUrl} handleCopySiteLinkClick={handleCopySiteLinkClick} />;
    }, [getSiteUrl, handleCopySiteLinkClick]);

    const cellRenderPdcEnabled = useCallback(({ data }: { data: { isPDCEnabled?: boolean | null } }) => {
        const value = data?.isPDCEnabled;

        if (value === true) {
            return "Yes";
        }

        if (value === false) {
            return "No";
        }

        return "";
    }, []);

    const columns = useMemo(() => [
        { field: "requestType", caption: "RequestType", minWidth: 120 },
        { field: "templateId", caption: "TemplateId", minWidth: 110 },
        { field: "templateName", caption: "TemplateName", minWidth: 130 },
        { field: "status", caption: "Status", minWidth: 100 },
        { field: "scriptDescription", caption: "ScriptDescription", minWidth: 150 },
        { field: "hqSiteName", caption: "SiteName", minWidth: 270, cellRender: cellRenderSiteName },
        { field: "appName", caption: "AppName", minWidth: 80 },
        { field: "hqSiteId", caption: "SiteId", minWidth: 100 },
        { field: "taxYearStartDate", caption: "TaxYearStartDate", minWidth: 170 },
        { field: "taxYearEndDate", caption: "TaxYearEndDate", minWidth: 170 },
        { field: "selectedLegalEntities", caption: "SelectedLegalEntities", minWidth: 130 },
        { field: "masterClientId", caption: "MasterClientId", minWidth: 130 },
        { field: "includeAllEntities", caption: "IncludeAllEntities", minWidth: 130 },
        {
            field: "isPDCEnabled",
            caption: "PDC Enabled",
            cellRender: cellRenderPdcEnabled,
            lookup: yesNoLookup,
            minWidth: 130,
        },
        { field: "fileName", caption: "FileName", minWidth: 170 },
        { field: "source", caption: "Source", minWidth: 130 },
        { field: "created", caption: "CreatedDate", minWidth: 170 },
        { field: "createdBy", caption: "CreatedBy", minWidth: 170 },
        { field: "lastModified", caption: "LastModifiedDate", minWidth: 170 },
        { field: "lastModifiedBy", caption: "LastModifiedBy", minWidth: 170 },
    ], [cellRenderPdcEnabled, cellRenderSiteName]);

    const onChangePagination = (e: any, pageNumber: number) => {
        setPageNumber(pageNumber);
    };

    const refreshButtonOptions = {
        icon: "refresh",
        onClick: () => {
            clearSelection();
            fetchData();
        },
    };

    const renderDetails = ({ row: { data } }: any) => {
        return data.status ? (
            <IconButton
                className="dx-datagrid dx-link"
                aria-label="See Details"
                title="See Details"
                onClick={() => handleSeeDetailsButton(data.id)}
            >
                <SearchIcon />
            </IconButton>
        ) : null;
    };

    const handleSeeDetailsButton = (id: string) => {
        setSiteId(id);
        setIsDisplayModal(true);
    };

    const handleClose = () => {
        setSiteId('');
        setIsDisplayModal(false);
    };

    const totalPages = useMemo(() => {
        return Math.ceil((gridData?.totalCount || 0) / defaultPageSize);
    }, [gridData?.totalCount]);

    const openLoadExcelPopup = () => {
        setBulkSiteMainPage(false);
    };

  const fetchData = async () => {
    try {        
      const startIndex =
        pageNumber === 1 ? 0 : (pageNumber - 1) * defaultPageSize;
      setSitesData(initialStateData);
      const sitesList = await createSitesClientApi.taxPortal_GetSitesGridData(
        startIndex,
        defaultPageSize,
        appName
      );
      setSitesData({
        pending: false,
        sites: UTCtoLocalTime(sitesList?.data?.data),
        totalCount: sitesList.data.totalCount,
      });
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    }
  };

  const fetchSitesBySearchTerms = async () => {
      setSitesData({ pending: true, sites: [], totalCount: 0 });
      try {
        const startIndex = pageNumber === 1 ? 0 : (pageNumber - 1) * defaultPageSize;
        const sitesList  = await createSitesClientApi.taxPortal_GetSitesGridDataBySearch(
          appName,
          defaultPageSize,
          startIndex,
          searchTerm
        );
        if (sitesList?.data) {
          setSitesData({pending: false, sites: UTCtoLocalTime(sitesList?.data?.data), totalCount: sitesList.data.totalCount});
        } else {
          setSitesData({ ...initialStateData, pending: false });
        }
      } catch (error) {
        showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        setSitesData(initialStateData);
      }
  };

     const onSelectionchanged = (e: SelectionChangedEvent) => {
        const newPageKeys = e.selectedRowKeys;
        const newPageData = e.selectedRowsData;
        const visibleKeys = e.component.getVisibleRows().map((row: any) => row.key)

        const addedKeys = newPageKeys.filter((key: any) => !prevKeysRef.current.includes(key));
        const addedData = newPageData.filter((row: any) => addedKeys.includes(row.id))
        const removedKeys = visibleKeys.filter((k: any) => prevKeysRef.current.includes(k) && !newPageKeys.includes(k))

        const updatedKeys = [
            ...prevKeysRef.current.filter((k: any) => !removedKeys.includes(k)),
            ...addedKeys,
        ]

        const updatedData = [
            ...prevDataRef.current.filter((row: any) => !removedKeys.includes(row.id)),
            ...addedData,
        ]
        prevKeysRef.current = updatedKeys;
        prevDataRef.current = updatedData;

        setSelectedRowKeys(updatedKeys)
        setSelectedRowData(updatedData)

        const hasSelection = updatedKeys.length > 0;
        const isAllScheduled =
            hasSelection &&
            updatedData.every(
                (row: any) => String(row?.status || "").trim().toUpperCase() === "SCHEDULED"
            );
        setIsCancelRequestEnabled(isAllScheduled);
    }
    const clearSelection = () => {
        setSelectedRowKeys([]);
        setSelectedRowData([]);
        setIsCancelRequestEnabled(false);
        prevKeysRef.current = [];
        prevDataRef.current = [];
    };

  const handleCancelRequest = async () => {
    const payload = selectedRowData.map((site: any) => ({
      siteName: site.hqSiteName,
      mdmClientId: site.masterClientId,
      appName: site.appName,
      status: site.status,
    }));
    try {
      const apiResults = await createSitesClientApi.taxPortal_CancelSiteRequests(
        userEmail,
        payload
      );
        if (apiResults) {
        clearSelection();
        fetchData();
      }
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    }
  };

    const UTCtoLocalTime = (siteData: any) => {
        return siteData?.map((row: any) => {
            return {
                ...row,
                created: convertUTCTimeToLocal(row.created),
                lastModified: convertUTCTimeToLocal(row.lastModified),
                taxYearStartDate: convertUTCTimeToLocal(row.taxYearStartDate),
                taxYearEndDate: convertUTCTimeToLocal(row.taxYearEndDate)
            }
        })
    }

    const handleExportToExcel = async () => {
      try {
        const selectedSiteIds = selectedRowData.map((site: any) => site.id);
        const response =  await createSitesClientApi.taxportal_ExportSelectedSitesToExcel(
            selectedSiteIds
          );
  
        if (response) {
          const blob = new Blob([response], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `${appName}-BulkSites_${new Date().toISOString().split("T")[0]}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
  
        showSnackbar({
          message: SnackbarMessage.EXPORT_TO_EXCEL_SUCCESS,
          severity: "success",
        });
      } catch (error) {
        showSnackbar({
          message: SnackbarMessage.EXPORT_TO_EXCEL_FAIL,
          severity: "error",
        });
      }
    };
  
    const exportButtonOptions = {
      icon: "exportxlsx",
      hint: "Export Selected Records to Excel",
      onClick: handleExportToExcel,
      disabled: !isExportEnabled,
      elementAttr: {
        title: isExportEnabled
          ? `Export ${selectedRowData.length} selected records to Excel`
          : "Select records to export",
      },
    };
  
    useEffect(() => {
      setIsExportEnabled(selectedRowData.length > 0);
    }, [selectedRowData]);
  
    useEffect(() => {
        if (!searchTerm) {
            fetchData();
        } else {
            clearSelection();
        }
    }, [pageNumber, searchTerm]);

    useEffect(() => {
        if (searchTerm) {
            clearSelection();
            fetchSitesBySearchTerms();
        }
    }, [searchTerm, pageNumber]);

    useEffect(() => {
        const handleSignalRMessage = (message: string) => {
            if (message && message.includes("records rejected")) {
                fetchData();
            }
        }
        hubConnection.on(signalRMethods.success, handleSignalRMessage);
        return () => {
            hubConnection.off(signalRMethods.success, handleSignalRMessage);
        }
    }, [hubConnection]);

    return (
        <div style={{ marginRight: "20px" }}>
            <DataGrid
                id="grid-bulksite"
                keyExpr="id"
                showBorders
                allowColumnResizing
                dataSource={gridData.sites || []}
                height={calculateGridHeight(260)}
                remoteOperations
                selectedRowKeys={selectedRowKeys}
                onSelectionChanged={onSelectionchanged}
                noDataText={gridData.pending ? "Loading..." : "No Data"}
            >
                <Selection
                    mode="multiple"
                    selectAllMode={"page"}
                    showCheckBoxesMode={"always"}
                />
                <Scrolling columnRenderingMode="virtual" />
                <SearchPanel
                    placeholder={"Global Search..."}
                    onTextChange={(e) => {
                        setSearchTerm(e);
                    }}
                    visible
                />
                <FilterRow visible />
                <HeaderFilter visible />
                <Paging enabled={false} />
                <Toolbar>
                    <Item location="start">
                        <div className="counter">
                            <h4>
                                {gridData.pending
                                    ? "Loading..." : `${selectedRowData.length} out of ${gridData.totalCount} records selected`}
                            </h4>
                        </div>
                    </Item>
                    <Item>
                        <Button variant="outlined" onClick={openLoadExcelPopup}>
                            Load Excel Requests
                        </Button>
                    </Item>
                    <Item>
                        <Button
                            variant="outlined"
                            disabled={!isCancelRequestEnabled}
                            onClick={handleCancelRequest}
                        >
                            Cancel Request
                        </Button>
                    </Item>
                    <Item  widget="dxButton" options={exportButtonOptions} location="after" />
                    <Item name="searchPanel" location="after" />
                    <Item
                        widget="dxButton"
                        options={refreshButtonOptions}
                        name="alignRight"
                    />
                    <Item name="columnChooserButton" showText="inMenu" />
                </Toolbar>

                {columns.map((col: any) => (
                    <Column
                        key={col.field}
                        dataField={col.field}
                        caption={col.caption}
                        minWidth={col.minWidth}
                        alignment="left"
                        {...(col.lookup ? { lookup: col.lookup } : {})}
                        {...(col.cellRender ? { cellRender: col.cellRender } : {})}
                    />
                ))}
                <Column
                    cellRender={renderDetails}
                    type="buttons"
                    caption="Execution detail"
                />
            </DataGrid>
            <div className="grid-pagination">
                <Pagination
                    page={pageNumber}
                    onChange={onChangePagination}
                    count={totalPages}
                    variant="outlined"
                    shape="rounded"
                />
            </div>
            <ModalBulkSiteCreationDetail
                handleClose={handleClose}
                isDisplayModal={isDisplayModal}
                siteId={siteId}
                userEmail={userEmail}
            />
        </div>
    );
};
